import { useState, useEffect } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Upload, Loader2, Scissors, Receipt, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CHUNK_SIZE = 3000;

function UploadCard({
  title,
  description,
  icon: Icon,
  count,
  loading,
  uploading,
  progress,
  onUpload,
  onDelete,
  uploadTestId,
  deleteTestId,
  fileTestId,
}) {
  return (
    <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-5">
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 rounded-lg bg-orange-50 flex items-center justify-center">
          <Icon className="h-6 w-6 text-[#C2410C]" />
        </div>
        <div>
          <h3 className="font-heading font-semibold text-stone-900">{title}</h3>
          <p className="text-2xl font-bold text-stone-900 font-mono leading-tight">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : (count ?? 0).toLocaleString()}
          </p>
        </div>
      </div>

      <div className="border-t border-stone-100 pt-4">
        <p className="text-xs text-stone-500 mb-3">{description}</p>
        <label className="cursor-pointer">
          <Input
            type="file"
            accept=".csv"
            className="hidden"
            onChange={onUpload}
            disabled={uploading}
            data-testid={fileTestId}
          />
          <Button
            data-testid={uploadTestId}
            className="bg-[#C2410C] hover:bg-[#9A3412] text-white"
            disabled={uploading}
            asChild
          >
            <span>
              {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? "Uploading..." : "Upload CSV"}
            </span>
          </Button>
        </label>
        {progress && (
          <p className="text-xs text-[#C2410C] mt-2 font-medium">{progress}</p>
        )}
      </div>

      {count > 0 && (
        <div className="border-t border-stone-100 pt-4">
          <Button
            data-testid={deleteTestId}
            variant="outline"
            className="border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700"
            onClick={onDelete}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete All ({(count ?? 0).toLocaleString()})
          </Button>
        </div>
      )}
    </div>
  );
}

export default function TrimsMasterPage() {
  const [bomCount, setBomCount] = useState(null);
  const [purchaseCount, setPurchaseCount] = useState(null);
  const [bomLoading, setBomLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState(true);
  const [bomUploading, setBomUploading] = useState(false);
  const [purchaseUploading, setPurchaseUploading] = useState(false);
  const [bomProgress, setBomProgress] = useState("");
  const [purchaseProgress, setPurchaseProgress] = useState("");

  const [testBarcode, setTestBarcode] = useState("");
  const [lookupResult, setLookupResult] = useState(null);
  const [lookupLoading, setLookupLoading] = useState(false);

  const fetchCounts = async () => {
    try {
      const [b, p] = await Promise.all([
        api.get("/trims/bom/count"),
        api.get("/trims/purchase/count"),
      ]);
      setBomCount(b.data.count);
      setPurchaseCount(p.data.count);
    } catch {} finally {
      setBomLoading(false);
      setPurchaseLoading(false);
    }
  };

  useEffect(() => { fetchCounts(); }, []);

  const uploadChunked = async (file, endpoint, setProgress) => {
    const text = await file.text();
    const lines = text.split("\n");
    const header = lines[0];
    const dataLines = lines.slice(1).filter((l) => l.trim());
    const totalRows = dataLines.length;
    const totalChunks = Math.max(1, Math.ceil(totalRows / CHUNK_SIZE));
    let processed = 0;
    let skipped = 0;
    for (let i = 0; i < totalChunks; i++) {
      const chunk = dataLines.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
      const csvChunk = header + "\n" + chunk.join("\n");
      const blob = new Blob([csvChunk], { type: "text/csv" });
      const formData = new FormData();
      formData.append("file", blob, "chunk.csv");
      setProgress(`Uploading batch ${i + 1} of ${totalChunks} (${Math.min((i + 1) * CHUNK_SIZE, totalRows).toLocaleString()} / ${totalRows.toLocaleString()} rows)...`);
      const res = await api.post(endpoint, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      processed += chunk.length;
      skipped += res.data?.skipped || 0;
    }
    return { processed, totalChunks, skipped };
  };

  const handleBomUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBomUploading(true);
    setBomProgress("Reading file...");
    try {
      const { processed, totalChunks } = await uploadChunked(file, "/trims/bom/upload-csv", setBomProgress);
      toast.success(`Imported ${processed.toLocaleString()} BOM rows in ${totalChunks} batch(es)`);
      fetchCounts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBomUploading(false);
      setBomProgress("");
      e.target.value = "";
    }
  };

  const handlePurchaseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPurchaseUploading(true);
    setPurchaseProgress("Reading file...");
    try {
      const { processed, totalChunks, skipped } = await uploadChunked(file, "/trims/purchase/upload-csv", setPurchaseProgress);
      const skipMsg = skipped > 0 ? ` (skipped ${skipped} invalid rows)` : "";
      toast.success(`Imported ${processed.toLocaleString()} purchase rows in ${totalChunks} batch(es)${skipMsg}`);
      fetchCounts();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurchaseUploading(false);
      setPurchaseProgress("");
      e.target.value = "";
    }
  };

  const handleBomDelete = async () => {
    if (!window.confirm("Delete ALL Trims BOM rows? This cannot be undone.")) return;
    try {
      const res = await api.delete("/trims/bom/all");
      toast.success(`Deleted ${res.data.deleted?.toLocaleString()} BOM rows`);
      fetchCounts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handlePurchaseDelete = async () => {
    if (!window.confirm("Delete ALL Trims Purchase rows? This cannot be undone.")) return;
    try {
      const res = await api.delete("/trims/purchase/all");
      toast.success(`Deleted ${res.data.deleted?.toLocaleString()} purchase rows`);
      fetchCounts();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleLookup = async () => {
    const bc = testBarcode.trim();
    if (!bc) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const res = await api.get(`/trims/lookup/${encodeURIComponent(bc)}`);
      setLookupResult(res.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLookupLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="trims-master-page">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">
          Trims Master
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          Upload Trims BOM and Trims Purchase data. When a design is tagged to a barcode, the system auto-fills the trims cost from the latest purchase rates.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <UploadCard
          title="Trims BOM"
          description="CSV columns: design_barcode, trims_barcode. Each row counts as 1 Qty — duplicate (design_barcode, trims_barcode) rows are kept so 2 same trims used in one design contribute 2× the rate. Re-uploads are additive; use 'Delete All' to start fresh."
          icon={Scissors}
          count={bomCount}
          loading={bomLoading}
          uploading={bomUploading}
          progress={bomProgress}
          onUpload={handleBomUpload}
          onDelete={handleBomDelete}
          uploadTestId="trims-bom-upload-button"
          deleteTestId="trims-bom-delete-button"
          fileTestId="trims-bom-file-input"
        />

        <UploadCard
          title="Trims Purchase"
          description="CSV columns: date, trims_barcode, rate, supplier_name, trims_name. Supplier and trim name help identify the trim in the lookup. The system uses the LATEST date's rate per trims barcode. Date formats accepted: YYYY-MM-DD, DD/MM/YYYY, DD-MM-YYYY."
          icon={Receipt}
          count={purchaseCount}
          loading={purchaseLoading}
          uploading={purchaseUploading}
          progress={purchaseProgress}
          onUpload={handlePurchaseUpload}
          onDelete={handlePurchaseDelete}
          uploadTestId="trims-purchase-upload-button"
          deleteTestId="trims-purchase-delete-button"
          fileTestId="trims-purchase-file-input"
        />
      </div>

      {/* Lookup test */}
      <div className="bg-white border border-stone-200 rounded-lg p-6 space-y-4 max-w-2xl">
        <div>
          <h3 className="font-heading font-semibold text-stone-900">Lookup Test</h3>
          <p className="text-xs text-stone-500 mt-1">Quickly test the trims sum for any design barcode.</p>
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="trims-lookup-input"
            placeholder="Enter a design barcode..."
            value={testBarcode}
            onChange={(e) => setTestBarcode(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
          />
          <Button
            data-testid="trims-lookup-button"
            className="bg-[#C2410C] hover:bg-[#9A3412] text-white shrink-0"
            disabled={!testBarcode.trim() || lookupLoading}
            onClick={handleLookup}
          >
            {lookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Lookup"}
          </Button>
        </div>
        {lookupResult && (
          <div className="border-t border-stone-100 pt-4" data-testid="trims-lookup-result">
            <div className="flex items-baseline justify-between mb-3">
              <span className="text-sm text-stone-500">Total Trims Cost</span>
              <span className="text-2xl font-bold text-[#C2410C] font-mono">
                ₹{(lookupResult.total || 0).toFixed(2)}
              </span>
            </div>
            {lookupResult.items?.length > 0 ? (
              <div className="space-y-1.5">
                {lookupResult.items.map((it, idx) => (
                  <div key={idx} className="flex items-start justify-between gap-3 text-sm border-b border-stone-50 pb-2 last:border-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline flex-wrap gap-x-2">
                        <span className="font-mono text-stone-700">{it.trims_barcode}</span>
                        <span className="text-xs text-stone-500">× {it.qty || 1}</span>
                        {!it.found && <span className="text-xs text-red-500">(no purchase rate)</span>}
                      </div>
                      {(it.trims_name || it.supplier_name) && (
                        <div className="text-xs text-stone-500 mt-0.5 truncate">
                          {it.trims_name && <span className="text-stone-700">{it.trims_name}</span>}
                          {it.trims_name && it.supplier_name && <span className="mx-1.5 text-stone-300">·</span>}
                          {it.supplier_name && <span>{it.supplier_name}</span>}
                        </div>
                      )}
                      {it.found && (
                        <div className="text-xs text-stone-400 mt-0.5">@ ₹{(it.rate || 0).toFixed(2)} · {it.date}</div>
                      )}
                    </div>
                    <span className="font-mono text-stone-900 shrink-0">₹{(it.line_total ?? it.rate ?? 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-stone-400">No BOM entries found for this barcode.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
